import { determineCategoryId } from '@/features/advertisement/utils/adsense/determineCategoryId'
import { determinePageNumber } from '@/features/advertisement/utils/adsense/determinePageNumber'
import { determineGeoId } from '@/features/advertisement/utils/adsense/locationIdToGeoIdMapping'
import { determinePageType } from '@/features/advertisement/utils/adsense/pageTypeMapping'
import { type GA_PAGE_TYPE } from '@/lib/ga/constants/datalayer'

export type CreateAdSenseChannelParams = {
  expInd?: '1' | '0' // 1 being used for NWA, 0 for legacy
  os?: 'w' | 'm' // web vs mweb
  pageType: GA_PAGE_TYPE
  l1Catid?: string
  l2Catid?: string
  ads?: string
  page?: number // SRP page number
  locationId: number
  expName?: string
  expGroup?: string
}

/**
 * Creates an AdSense channel based on the provided parameters.
 *
 * @param {CreateAdsenseChannelParams} params - The parameters for creating the AdSense channel.
 * @returns {string} The generated AdSense channel.
 *
 * @example
 * createAdsenseChannel({
 *   pageType: GA_PAGE_TYPE.VIEW_AD,
 *   l1Catid: 27,
 *   l2Catid: 174,
 *   locationId: 91,
 *   expName: 'kcan',
 *   expGroup: 'a',
 * }); // Returns '0_w_vip_27_174_x_1_91_x_kcan_a'
 */
export function createAdSenseChannel({
  expInd = '0',
  os = 'w',
  pageType,
  l1Catid,
  l2Catid,
  ads = 'x',
  page = 1,
  locationId,
  expName = 'x',
  expGroup,
}: CreateAdSenseChannelParams): string {
  const pageTypeId = determinePageType(pageType)

  const categoryId = determineCategoryId(l1Catid, l2Catid)
  const geoId = determineGeoId(locationId)
  const pageBin = determinePageNumber(page, pageType)

  const channelComponents =
    expInd === '0'
      ? [expInd, os, pageTypeId, categoryId, ads, pageBin, geoId, expName, expGroup]
      : [expInd, os, 'x', 'x_x', 'x', 'x', 'x', expName, expGroup]

  const channel = channelComponents.filter(Boolean).join('_').toLowerCase()

  return channel
}
